import { History } from 'lucide-react';

import { useEntityActivity } from '@/hooks/useActivityLog';

import { ActivityEntry } from './ActivityEntry';

/**
 * One entity's own change history, for embedding on that entity's edit page.
 *
 * Drop-in for any module: `<EntityActivityPanel entityType="workflow" entityId={id} />`
 * works the moment that module starts producing entries.
 */
export const EntityActivityPanel = ({
  entityType,
  entityId,
  limit = 50,
}: {
  entityType: string;
  entityId: string | undefined;
  limit?: number;
}) => {
  const { data, isLoading, isError } = useEntityActivity(entityType, entityId, limit);

  if (isLoading) {
    return <p className="text-muted-foreground py-6 text-sm">Loading history…</p>;
  }

  if (isError) {
    return (
      <p className="text-muted-foreground py-6 text-sm">
        Could not load the change history. The activity log is restricted to administrators.
      </p>
    );
  }

  const entries = data?.data ?? [];

  if (entries.length === 0) {
    return (
      <div className="text-muted-foreground flex flex-col items-center gap-2 py-10 text-sm">
        <History className="h-6 w-6 opacity-50" />
        <p>No changes recorded yet.</p>
        <p className="text-xs">Edits made from here on will appear in this list.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {entries.map((entry) => (
        <ActivityEntry key={entry.id} entry={entry} showEntity={false} />
      ))}
      {data && data.pagination.total > entries.length && (
        <p className="text-muted-foreground pt-1 text-xs">
          Showing the {entries.length} most recent of {data.pagination.total} changes.
        </p>
      )}
    </div>
  );
};
